'use client'

import { ChangeEvent, FC, useState, useEffect, useRef } from 'react'
import cn from 'classnames'
import { useSearchParams } from 'next/navigation'
import { Banner, Button } from '@/root/ui/tuuleliinid'
import { useGetTranslations, useSignUp, useCustomRouter } from '@/root/shared-hooks'
import { AUTH_HEADING_ID, AuthVariant, TuuleliinidAuthMethod, isEstonianMethod } from '@/root/modules/tuuleliinid/LoginPage/constants'
import { getAuthErrorMessage } from '@/root/modules/tuuleliinid/LoginPage/helpers'
import { EmailForm } from '@/root/modules/tuuleliinid/LoginPage/components/EmailForm/EmailForm'
import { AuthMethodSelector } from '@/root/modules/tuuleliinid/LoginPage/components/AuthMethodSelector/AuthMethodSelector'
import { RegistrationBenefits } from '@/root/modules/tuuleliinid/LoginPage/components/RegistrationBenefits/RegistrationBenefits'
import { IdKaartAuth } from '@/root/modules/tuuleliinid/LoginPage/components/IdKaartAuth'
import { MobileIdAuth } from '@/root/modules/tuuleliinid/LoginPage/components/MobileIdAuth'
import { SmartIdAuth } from '@/root/modules/tuuleliinid/LoginPage/components/SmartIdAuth'
import { EmailConfirmation } from '@/root/modules/tuuleliinid/LoginPage/components/EmailConfirmation/EmailConfirmation'

interface AuthSectionProps {
  variant: AuthVariant
}

export const AuthSection: FC<AuthSectionProps> = ({ variant }) => {
  const { getTranslation } = useGetTranslations()
  const { locale, push, replace } = useCustomRouter()
  const [authMethod, setAuthMethod] = useState<TuuleliinidAuthMethod>('email')
  const [isSignupComplete, setIsSignupComplete] = useState(false)
  const searchParams = useSearchParams()
  const hash = searchParams.get('hash')
  const isActivated = searchParams.get('activated') === 'true'
  const { activateEmail, isLoading: isActivating, error: activateError } = useSignUp()
  const [activationSuccess, setActivationSuccess] = useState(isActivated)
  const [isIdKaartLoading, setIsIdKaartLoading] = useState(false)
  const hasActivated = useRef(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const section = sectionRef.current
    if (!section) return
    const rect = section.getBoundingClientRect()
    if (rect.bottom > window.innerHeight) {
      const heading = document.getElementById(AUTH_HEADING_ID)
      ;(heading ?? section).scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  useEffect(() => {
    if (hash && variant === 'login' && !hasActivated.current) {
      hasActivated.current = true
      activateEmail(hash).then((result) => {
        if (result.success) {
          setActivationSuccess(true)
          replace(`/${locale}/auth/login?activated=true`, { scroll: false })
        }
      })
    }
  }, [hash, variant, activateEmail, locale, replace])

  const handleAuthMethodChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAuthMethod(event.target.value as TuuleliinidAuthMethod)
  }

  const handleSignupSuccess = () => {
    setIsSignupComplete(true)
  }

  const isSignup = variant === 'signup'

  return (
    <div ref={sectionRef} className="xxs:px-7 flex w-full flex-col items-center px-4 py-7">
      <div
        className={cn(
          'flex min-h-105 w-full flex-col gap-8',
          !isEstonianMethod(authMethod) ? 'lg:flex-row lg:gap-0' : 'items-center justify-center'
        )}
      >
        <div
          className={cn('w-full', isEstonianMethod(authMethod) ? 'mx-auto max-w-md' : 'lg:w-1/2 lg:shrink-0 lg:pe-10')}
        >
          {isSignup && !isSignupComplete && (
            <>
              {!isEstonianMethod(authMethod) && (
                <h3 className="text-primary-base mb-4 text-xl font-bold">{getTranslation('authYourData')}</h3>
              )}

              {!isEstonianMethod(authMethod) && <EmailForm variant={variant} onSignupSuccess={handleSignupSuccess} />}
              {authMethod === 'id-kaart' && <IdKaartAuth variant={variant} onLoadingChange={setIsIdKaartLoading} />}
              {authMethod === 'smart-id' && <SmartIdAuth variant={variant} onLoadingChange={setIsIdKaartLoading} />}
              {authMethod === 'mobiil-id' && <MobileIdAuth variant={variant} onLoadingChange={setIsIdKaartLoading} />}
            </>
          )}

          {isSignup && isSignupComplete && <EmailConfirmation />}

          {!isSignup && (
            <>
              {!isEstonianMethod(authMethod) && (
                <h3 className="text-primary-base mb-4 text-xl font-bold">{getTranslation('authLoginEmailTitle')}</h3>
              )}

              {isActivating && (
                <Banner variant="info" align="left" body={getTranslation('authActivating')} className="mb-4" />
              )}
              {activationSuccess && (
                <Banner
                  variant="success"
                  align="left"
                  body={getTranslation('authActivationSuccess')}
                  className="mb-4"
                />
              )}
              {activateError && (
                <Banner
                  variant="error"
                  align="left"
                  body={getAuthErrorMessage(activateError, getTranslation)}
                  className="mb-4"
                />
              )}

              {!isEstonianMethod(authMethod) && <EmailForm variant={variant} />}
              {authMethod === 'id-kaart' && <IdKaartAuth variant={variant} onLoadingChange={setIsIdKaartLoading} />}
              {authMethod === 'smart-id' && <SmartIdAuth variant={variant} onLoadingChange={setIsIdKaartLoading} />}
              {authMethod === 'mobiil-id' && <MobileIdAuth variant={variant} onLoadingChange={setIsIdKaartLoading} />}
            </>
          )}
        </div>

        {!isEstonianMethod(authMethod) && (
          <div className="border-base-medium-light w-full border-t pt-6 lg:w-1/2 lg:border-s lg:border-t-0 lg:ps-10 lg:pt-0">
            {!isSignup && (
              <h3 className="text-primary-base mb-4 text-xl font-bold">
                {getTranslation('authRegistrationBenefitsHeading')}
              </h3>
            )}

            <RegistrationBenefits />

            {!isSignup && (
              <Button
                type="button"
                onClick={() => push(`/${locale}/auth/signup`)}
                variant="default"
                className="border-neutral-dark text-neutral-medium-light hover:border-primary-base hover:text-primary-base mt-6 flex h-12 w-full items-center justify-center rounded-lg border text-lg font-medium tracking-[0.75px] transition-colors"
              >
                {getTranslation('authCreateAccount')}
              </Button>
            )}
          </div>
        )}
      </div>

      <hr className="border-base-medium-light my-6 w-full" />

      <AuthMethodSelector
        authMethod={authMethod}
        onAuthMethodChange={handleAuthMethodChange}
        disabled={isIdKaartLoading}
      />
    </div>
  )
}
